import { useState } from 'react';
import { Loader2, MapPin, Hash, CheckCircle, AlertCircle, Navigation, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAddDevice } from '@/hooks/useAddDevice';
import { useNearbyDevices } from '@/hooks/useNearbyDevices';
import { accessControlAction } from '@/lib/api';

type Tab = 'code' | 'nearby';

interface AddDeviceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddDeviceModal({ open, onOpenChange }: AddDeviceModalProps) {
  const [tab, setTab] = useState<Tab>('code');
  const [deviceCode, setDeviceCode] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState<string | null>(null);
  const [sendingRequest, setSendingRequest] = useState<string | null>(null);

  const addDevice = useAddDevice();
  const {
    nearbyDevices,
    geoError,
    locating,
    loadingDevices,
    requestLocation,
  } = useNearbyDevices();

  const handleAddByCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deviceCode.trim()) return;

    setError(null);
    setSuccess(null);

    try {
      const result = await addDevice.mutateAsync({ deviceInput: deviceCode });
      if (result.alreadyLinked) {
        setSuccess('Enheten är redan kopplad till ditt konto');
      } else if (result.enrollmentResult === 'pending_approval') {
        setSuccess('Förfrågan skickad! En administratör behöver godkänna din åtkomst.');
      } else {
        setSuccess('Enheten har lagts till och är redo att användas');
      }
      setDeviceCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte lägga till enhet');
    }
  };

  const handleRequestAccess = async (deviceId: string) => {
    setSendingRequest(deviceId);
    setError(null);

    try {
      // Try to add device via the same flow (checks policy, creates assignment or request)
      const result = await addDevice.mutateAsync({ deviceInput: deviceId, setAsPrimary: false });

      if (result.enrollmentResult === 'pending_approval') {
        setRequestSent(deviceId);
        setSuccess('Förfrågan skickad till administratören');
      } else {
        setRequestSent(deviceId);
        setSuccess('Enheten har lagts till');
      }
    } catch {
      // Fallback: create access request directly (e.g. if user has no org yet)
      try {
        const { error: reqError } = await accessControlAction('create-request', {
          device_id: deviceId,
          requested_reason: 'Närliggande enhet — begäran via geolocation',
        });

        if (reqError) {
          setError(reqError);
        } else {
          setRequestSent(deviceId);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Kunde inte skicka förfrågan');
      }
    } finally {
      setSendingRequest(null);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setDeviceCode('');
      setSuccess(null);
      setError(null);
      setRequestSent(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card/95 backdrop-blur-xl border-border max-w-sm mx-auto">
        <DialogHeader>
          <DialogTitle>Lägg till enhet</DialogTitle>
          <DialogDescription>
            Ange en enhetskod eller hitta enheter nära dig
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          <button
            onClick={() => setTab('code')}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              tab === 'code'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Hash className="h-4 w-4" />
            Enhetskod
          </button>
          <button
            onClick={() => {
              setTab('nearby');
              requestLocation();
            }}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              tab === 'nearby'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <MapPin className="h-4 w-4" />
            Nära mig
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Success */}
        {success && (
          <div className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${
            success.includes('administratör')
              ? 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400'
              : 'border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-400'
          }`}>
            {success.includes('administratör') ? (
              <Clock className="h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle className="h-4 w-4 shrink-0" />
            )}
            {success}
          </div>
        )}

        {/* Tab: Code */}
        {tab === 'code' && (
          <form onSubmit={handleAddByCode} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="device-code" className="text-sm font-medium">
                Enhetskod
              </label>
              <input
                id="device-code"
                type="text"
                value={deviceCode}
                onChange={(e) => setDeviceCode(e.target.value.toUpperCase())}
                placeholder="T.ex. U77"
                autoFocus
                maxLength={20}
                className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-center text-lg font-mono tracking-widest ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <p className="text-xs text-muted-foreground text-center">
                Koden finns på enheten eller gavs av din administratör
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={addDevice.isPending || !deviceCode.trim()}
            >
              {addDevice.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Lägg till'
              )}
            </Button>
          </form>
        )}

        {/* Tab: Nearby */}
        {tab === 'nearby' && (
          <div className="space-y-3">
            {locating && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                Bestämmer din position...
              </div>
            )}

            {geoError && (
              <div className="text-center py-6 space-y-3">
                <Navigation className="h-8 w-8 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">{geoError}</p>
                <Button variant="outline" size="sm" onClick={requestLocation}>
                  Försök igen
                </Button>
              </div>
            )}

            {!locating && !geoError && loadingDevices && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                Söker efter enheter...
              </div>
            )}

            {!locating && !geoError && !loadingDevices && nearbyDevices.length === 0 && (
              <div className="text-center py-6">
                <MapPin className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Inga enheter hittades i närheten
                </p>
                <Button variant="outline" size="sm" className="mt-3" onClick={requestLocation}>
                  Sök igen
                </Button>
              </div>
            )}

            {nearbyDevices.length > 0 && (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {nearbyDevices.map((device) => (
                  <div
                    key={device.device_id}
                    className="flex items-center justify-between rounded-lg border border-border bg-background/50 p-3"
                  >
                    <div>
                      <p className="text-sm font-medium">{device.device_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {device.location || device.device_code}
                        {' · '}
                        {Math.round(device.distance_meters)}m bort
                      </p>
                    </div>
                    {requestSent === device.device_id ? (
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                        Skickad
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRequestAccess(device.device_id)}
                        disabled={sendingRequest === device.device_id}
                      >
                        {sendingRequest === device.device_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Begär åtkomst'
                        )}
                      </Button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-muted-foreground text-center pt-2">
                  Din förfrågan skickas till administratören för godkännande
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
